/* ============================================================================
   JAYLIS LEXIKON — progressive enhancement for /pages/inhaltsstoffe.
   Filter pills (Wirk/Basis/Hilfs), live search, A-Z scroll, accordion.
   Server-rendered list is already in the DOM — we just toggle visibility.
   ============================================================================ */
(function () {
  'use strict';

  function init() {
    const root = document.querySelector('.jlx-section');
    if (!root) return;

    const items = Array.from(root.querySelectorAll('[data-jlx-item]'));
    if (!items.length) return;

    const filterButtons = Array.from(root.querySelectorAll('[data-jlx-filter]'));
    const searchInput = root.querySelector('[data-jlx-search]');
    const alphaButtons = Array.from(root.querySelectorAll('[data-jlx-alpha-btn]'));
    const empty = root.querySelector('[data-jlx-empty]');
    const letters = Array.from(root.querySelectorAll('.jlx-letter'));

    /* ---- Counts ---- */
    const counts = { all: items.length, wirkstoffe: 0, basisstoffe: 0, hilfsstoffe: 0 };
    items.forEach((it) => {
      const cats = (it.dataset.cats || '').split(/\s+/);
      cats.forEach((c) => {
        if (counts[c] !== undefined) counts[c] += 1;
      });
    });
    const countAll = root.querySelector('[data-jlx-count-all]');
    if (countAll) countAll.textContent = String(counts.all);
    root.querySelectorAll('[data-jlx-count]').forEach((el) => {
      const key = el.dataset.jlxCount;
      if (counts[key] !== undefined) el.textContent = String(counts[key]);
    });

    /* ---- Empty letters muted ---- */
    const lettersWithEntries = new Set(items.map((i) => i.dataset.letter));
    alphaButtons.forEach((btn) => {
      const l = btn.dataset.jlxAlphaBtn;
      if (!lettersWithEntries.has(l)) btn.classList.add('is-empty');
    });

    /* ---- Filter state ---- */
    let activeFilter = 'all';
    let activeQuery = '';

    function applyFilter() {
      const q = activeQuery.trim().toLowerCase();
      let visibleCount = 0;
      items.forEach((it) => {
        const cats = (it.dataset.cats || '').split(/\s+/);
        const matchesCat = activeFilter === 'all' || cats.includes(activeFilter);
        const name = it.dataset.name || '';
        const inci = it.dataset.inci || '';
        const matchesQ = !q || name.includes(q) || inci.includes(q);
        const shown = matchesCat && matchesQ;
        it.classList.toggle('is-hidden', !shown);
        if (shown) visibleCount += 1;
      });
      // Hide letter sections whose every item is hidden
      letters.forEach((sec) => {
        const visible = sec.querySelectorAll('[data-jlx-item]:not(.is-hidden)').length;
        sec.classList.toggle('is-hidden', visible === 0);
      });
      if (empty) empty.hidden = visibleCount > 0;
    }

    /* ---- Filter pills ---- */
    filterButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        filterButtons.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        activeFilter = btn.dataset.jlxFilter;
        applyFilter();
      });
    });

    /* ---- Search ---- */
    if (searchInput) {
      let t = null;
      searchInput.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          activeQuery = searchInput.value;
          applyFilter();
        }, 80);
      });
    }

    /* ---- A-Z scroll: native via anchor href, plus close any open items ---- */
    alphaButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        if (btn.classList.contains('is-empty')) {
          e.preventDefault();
          return;
        }
      });
    });

    /* ---- Accordion ---- */
    items.forEach((it) => {
      const head = it.querySelector('.jlx-item__head');
      const body = it.querySelector('.jlx-item__body');
      if (!head || !body) return;
      head.addEventListener('click', () => {
        const open = head.getAttribute('aria-expanded') === 'true';
        head.setAttribute('aria-expanded', open ? 'false' : 'true');
        body.hidden = open;
      });
    });

    /* ---- Cursor-following hover preview ---- */
    const preview = root.querySelector('[data-jlx-cursor-preview]');
    const previewImg = preview ? preview.querySelector('img') : null;
    if (preview && previewImg && window.matchMedia('(hover: hover)').matches) {
      let rafId = 0;
      let pendingX = 0;
      let pendingY = 0;
      let currentItem = null;
      let currentSrc = '';

      function position() {
        rafId = 0;
        const previewW = preview.offsetWidth || 130;
        const previewH = preview.offsetHeight || 130;
        let x = pendingX;
        let y = pendingY - 18;            // 18px gap above pointer
        let translateY = '-100%';
        if (y - previewH < 8) {
          // Not enough room above — flip below.
          y = pendingY + 18;
          translateY = '0';
        }
        // Clamp horizontally so the preview never clips the viewport edges.
        const halfW = previewW / 2;
        if (x - halfW < 8) x = halfW + 8;
        if (x + halfW > window.innerWidth - 8) x = window.innerWidth - halfW - 8;
        preview.style.left = x + 'px';
        preview.style.top = y + 'px';
        preview.style.transform = `translate(-50%, ${translateY})`;
      }

      function onMove(e) {
        pendingX = e.clientX;
        pendingY = e.clientY;
        if (!rafId) rafId = requestAnimationFrame(position);
      }

      function show(src) {
        if (src !== currentSrc) {
          previewImg.src = src;
          currentSrc = src;
        }
        preview.classList.add('is-visible');
      }
      function hide() {
        preview.classList.remove('is-visible');
      }

      items.forEach((it) => {
        const src = it.dataset.previewSrc;
        if (!src) return;
        // Preview shows only on collapsed rows. Once a row is open, its
        // inline body image takes over and the floating preview hides —
        // both on enter, on every mousemove (in case the row was opened
        // while the pointer was already inside it) and the moment the
        // accordion toggles.
        const head = it.querySelector('.jlx-item__head');
        const isOpen = () => head && head.getAttribute('aria-expanded') === 'true';

        it.addEventListener('mouseenter', (e) => {
          if (isOpen()) return;
          show(src);
          pendingX = e.clientX;
          pendingY = e.clientY;
          position();
        });
        it.addEventListener('mousemove', (e) => {
          if (isOpen()) {
            hide();
            return;
          }
          onMove(e);
          // Re-show in case it was hidden after the row was previously open
          // and then closed again with the pointer still inside.
          if (!preview.classList.contains('is-visible')) show(src);
        });
        it.addEventListener('mouseleave', hide);

        // Hide instantly the moment the row opens.
        if (head) {
          head.addEventListener('click', () => {
            // setTimeout 0 so the accordion's own click handler runs first
            // and aria-expanded reflects the new state.
            setTimeout(() => {
              if (isOpen()) hide();
            }, 0);
          });
        }
      });
    }

    /* ---- Deep-link: open #ing-<handle> on load ---- */
    function openHash() {
      const h = (location.hash || '').replace(/^#/, '');
      if (!h) return;
      const target = document.getElementById(h);
      if (!target) return;
      // If it's an ingredient body or item itself
      const item = target.closest('[data-jlx-item]') || (h.startsWith('ing-') ? document.getElementById(h) : null);
      if (item) {
        const head = item.querySelector('.jlx-item__head');
        const body = item.querySelector('.jlx-item__body');
        if (head && body) {
          head.setAttribute('aria-expanded', 'true');
          body.hidden = false;
        }
        item.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    window.addEventListener('load', openHash);
    window.addEventListener('hashchange', openHash);

    /* ---- Init ---- */
    applyFilter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
